import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege.gen';
import { ajax } from 'cadenza/api-client/api';
import type { WorkbookDto, WorkbookListingDto } from 'cadenza/api-client/dto/workbook-dto';
import type { WorkbookVersionDto } from 'cadenza/api-client/dto/workbook-version-dto';
import { type TableData, type TableParams, tableParams } from 'cadenza/api-client/tables-api';
import { MIMETYPE_JSON } from 'cadenza/model/media-type';
import type { ProjectId } from 'cadenza/repository/model/project-id.gen';
import type { ProjectName } from 'cadenza/repository/model/project-name.gen';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';
import type { WorkbookName } from 'cadenza/repository/model/workbook-name.gen';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';
import type { MutableWorkbookSnapshotDto, WorkbookSnapshotDto } from 'cadenza/workbook/api/workbook-snapshot-dto';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';

/** Functions that use the management endpoints. To work with individual workbooks, use `workbook-api`. */

type WorkbookTableParams = TableParams & {
  projectId?: ProjectId;
  privilege?: ObjectPrivilege;
};

export function getWorkbooks(params: WorkbookTableParams = {}): Promise<TableData<WorkbookListingDto>> {
  const urlSearchParams = tableParams(params);
  if (params.projectId) {
    urlSearchParams.append('repositoryName', params.projectId.repositoryName);
    urlSearchParams.append('projectName', params.projectId.itemName);
  }
  urlSearchParams.append('privilege', params.privilege ?? 'visibleRestricted');
  return ajax<TableData<WorkbookDto>>(`/workbooks?${urlSearchParams}`, { dataType: 'json' });
}

export function getAllWorkbookSnapshots(params: TableParams): Promise<TableData<WorkbookSnapshotDto>> {
  return ajax<TableData<WorkbookSnapshotDto>>(`/workbook-snapshots?${tableParams(params)}`, { dataType: 'json' });
}

export function getWorkbookVersions({ repositoryName, itemName }: WorkbookId): Promise<WorkbookVersionDto[]> {
  return ajax(`/repositories/${repositoryName}/workbooks/${itemName}/versions`);
}

export function getWorkbookVersionDto(
  workbookName: WorkbookName,
  repositoryName: RepositoryName,
  version: number,
): Promise<WorkbookVersionDto> {
  return ajax(`/repositories/${repositoryName}/workbooks/${workbookName}/versions/${version}`);
}

export function deleteWorkbooks(workbookIds: WorkbookId[]) {
  return ajax<void>('/workbooks', {
    type: 'DELETE',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(workbookIds),
  });
}

export async function createNewWorkbook(repositoryName: RepositoryName, projectName?: ProjectName) {
  return ajax<WorkbookNameAndHash>('/workbooks', {
    method: 'POST',
    data: JSON.stringify({ repositoryName, projectName }),
    contentType: MIMETYPE_JSON,
  });
}

export interface WorkbookVersionData extends WorkbookVersionDto {
  isCurrentState?: boolean;
  isUnsaved?: boolean;
  isChanged?: boolean;
}

/**
 * Create the workbook part of the public workbook URL.
 *
 * @param workbookId - workbook name + repository name.
 * @param worksheetId - ID of worksheet to navigate to.
 * @return URL component for workbook part.
 */
export function createPublicWorkbookUrl(workbookId: WorkbookId, worksheetId?: WorksheetId) {
  return cadenzaUrl(
    `/public/repositories/${workbookId.repositoryName}/workbooks/${workbookId.itemName}${
      worksheetId ? `/worksheets/${worksheetId}` : ''
    }`,
  );
}

export function createWorkbookSnapshot(
  workbookId: WorkbookId,
  data: MutableWorkbookSnapshotDto & {
    workbookHash: string;
    worksheetId: string;
  },
) {
  return ajax<WorkbookSnapshotDto>(
    `/repositories/${workbookId.repositoryName}/workbooks/${workbookId.itemName}/snapshots`,
    { type: 'POST', data: JSON.stringify(data), contentType: MIMETYPE_JSON },
  );
}

export function getWorkbookSnapshots({ repositoryName, itemName }: WorkbookId) {
  return ajax<WorkbookSnapshotDto[]>(`/repositories/${repositoryName}/workbooks/${itemName}/snapshots`, {
    contentType: MIMETYPE_JSON,
  });
}

export function updateWorkbookSnapshot(
  { repositoryName, itemName }: WorkbookId,
  patch: Partial<MutableWorkbookSnapshotDto>,
) {
  return ajax<void>(`/repositories/${repositoryName}/workbooks/${itemName}/snapshots`, {
    type: 'PATCH',
    data: JSON.stringify(patch),
    contentType: MIMETYPE_JSON,
  });
}

export type WorkbookSnapshotBatchEditDto =
  | Pick<WorkbookSnapshotDto, 'deletionDate'>
  | {
      workbookIds: WorkbookId[];
    };

export function updateWorkbookSnapshotsDeletionDate(dto: WorkbookSnapshotBatchEditDto) {
  ajax<void>('/workbook-snapshots', {
    type: 'PATCH',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(dto),
  });
}

// note: deleting snapshots uses deleteWorkbook, so there is no deleteWorkbookSnapshot
